import { useState } from 'react';
import { createPortal } from 'react-dom';
import { fmtLongDay, fmtTime } from '../lib/time';
import { alert, haptic } from '../lib/telegram';
import { useT } from '../lib/i18n';
import { api } from '../screens/client/api';

/**
 * Moving a session, or a whole weekly series.
 *
 * Only the time of day changes — each session keeps its own date. That is
 * what "move my Wednesdays to 12:00" actually means, and it's why this
 * takes a time rather than a full date.
 *
 * Credits are never touched: the session moves, the booking stays. The
 * client gets one "moved" message rather than a cancellation followed by
 * a new confirmation.
 */
/** A booking that is no longer what the screen shows: refetch, don't report it as a failure. */
export const STALE = new Set(['unknown_booking', 'booking_gone', 'already_cancelled', 'not_in_session']);

export function MoveSheet({ booking, name, isSeries, to, onClose, onDone }: {
  booking: any; name: string; isSeries: boolean;
  onClose: () => void; onDone: () => void;
  /** Calendar → «Неделя», a block dragged: the new day and time, filled in. */
  to?: string;
}) {
  const t = useT();
  const current = new Date(booking.starts_at);
  const target0 = to ? new Date(to) : current;
  const hh = String(target0.getHours()).padStart(2, '0');
  const mm = String(target0.getMinutes()).padStart(2, '0');

  const [time, setTime] = useState(`${hh}:${mm}`);
  const [date, setDate] = useState(to
    ? `${target0.getFullYear()}-${String(target0.getMonth() + 1).padStart(2, '0')}-${String(target0.getDate()).padStart(2, '0')}`
    : current.toISOString().slice(0, 10));
  const [wholeSeries, setWholeSeries] = useState(isSeries);
  const [busy, setBusy] = useState(false);

  async function go() {
    setBusy(true);
    try {
      const [h, m] = time.split(':').map(Number);
      // A series keeps its dates; a single session can change day.
      const target = wholeSeries ? new Date(current) : new Date(date + 'T12:00:00');
      target.setHours(h, m, 0, 0);

      const res = await api<{ moved: number; skipped: any[] }>(
        `/api/coach/bookings/${booking.booking_id}/reschedule`,
        { method: 'POST', body: JSON.stringify({
            starts_at: target.toISOString(), whole_series: wholeSeries }) });

      haptic.success();
      await alert(
        t('co.cd.moved', { n: res.moved, sessions: t.word('co.today.sessions', res.moved), time }) +
        (res.skipped.length ? t('co.cd.movedSkipped', { n: res.skipped.length }) : '') +
        t('co.cd.toldThem'));
      onDone();
      onClose();
    } catch (err: any) {
      haptic.error();
      // The lesson changed since this screen loaded (cancelled, or the seat
      // given to someone else): fetched again, so the next tap is on what is there now.
      if (STALE.has(err.message)) {
        await alert(t('co.cd.stale'));
        onDone();
        onClose();
        return;
      }
      await alert(err.message === 'nothing_could_move'
        ? t('co.cd.timeTaken')
        : t('co.cd.couldNotMove', { message: err.message }));
    } finally { setBusy(false); }
  }

  return createPortal(
    <div className="sheet">
      <div className="sheet__panel">
        <div className="sheet__title">{t('co.cd.moveTitle', { name })}</div>
        <div className="card__when">
          {t('co.cd.moveCurrently', { day: fmtLongDay(current), time: fmtTime(booking.starts_at) })}
        </div>

        {!wholeSeries && (
          <>
            <div className="sheet__label">{t('co.cd.newDay')}</div>
            <input type="date" className="paybar__opt" style={{ width: '100%' }}
              value={date} onChange={(e) => setDate(e.target.value)} />
          </>
        )}
        <div className="sheet__label">{t('co.cd.newTime')}</div>
        <input type="time" className="paybar__opt" style={{ width: '100%' }}
          value={time} onChange={(e) => setTime(e.target.value)} />

        {isSeries && (
          <label className="checkline" style={{ marginTop: 12 }}>
            <input type="checkbox" checked={wholeSeries}
              onChange={(e) => setWholeSeries(e.target.checked)} />
            <span>
              {t('co.cd.moveWholeSeries')}
              <small>{t('co.cd.wholeSeries')}</small>
            </span>
          </label>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button className="paybar__opt" data-back style={{ flex: 1 }} onClick={onClose}>{t('co.cd.back')}</button>
          <button className="paybar__opt"
            style={{ flex: 2, background: 'var(--accent)', color: 'var(--accent-text)', borderColor: 'var(--accent)' }}
            disabled={busy} onClick={go}>
            {busy ? t('co.cd.moving') : t('co.cd.moveTo', { time })}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
